import { EnumLegalEntityRole } from "../model/legal-entity-basic-info";
import { EnumUserRole } from "../model/user-basic-info";
import { EnumUserLegalEntityRole } from "../model/user-legal-entity-info";


export class SharedUtils {

    static inputValueIsEmpty(value: any | undefined): boolean {
        // we don't check for string here so it also works with arrays
        return (value === null || value === undefined || value.length === 0 || value === '' || value === 0);
    }

    static inputValueIsPresent(value: any | undefined): boolean {
        // we don't check for string here so it also works with arrays
        return !(value === null || value === undefined || value.length === 0 || value === '' || value === 0);
    }

    static breakString(text: string, separator: string) {
        let example: string = "Un esempio di stringa strana... Ciao. con il punto.  in vari punti."; 
        // console.log("SHARED UTILS - Breaking a String... ORIGINAL: " + example + 
        // "\n RESULT: " + example.replace("\b\d_[a-zA-Z]+_" + separator + "\ \b/g", separator + " "));
        return text.replace("\b\d_[a-zA-Z]+_" + separator + "\ \b/g", separator + " "); 
    }

    static removeSpecialChars(origin: string): string {
        let result: string = origin.replace(/[^a-zA-Z]/g , "");
        result = result.trim();
        result = result.toLowerCase();
        return result;
    }

    static cleanModals(): void {
        //get all open modals
        const modals: NodeListOf<Element> = document.querySelectorAll('.modal.show');

        modals.forEach( (modalElement: Element) => {
            // Use Bootstrap's native modal API to properly hide
            const modal: any = (window as any).bootstrap.Modal.getInstance(modalElement);
            if(modal) {
                modal.hide();
            }
            // Fallback: manual cleanup
            modalElement.classList.remove('show');
            (modalElement as HTMLElement).style.display = 'none';
        });

        // remove all backdrop
        document.querySelectorAll('.modal-backdrop').forEach( el => el.remove());

        // Clean up body classes and styles
        document.body.classList.remove('modal-open');
        document.body.style.overflow = '';
        document.body.style.paddingRight = '';
    }

    private static async getHashString(myString: string): Promise<string> {
        const encoder: TextEncoder = new TextEncoder();
        const data = encoder.encode(myString);
        const hashBuffer: ArrayBuffer = await crypto.subtle.digest('SHA-256', data);
        return btoa(String.fromCharCode(...new Uint8Array(hashBuffer)));
    }

    private static async deriveKeyFromPassword(password: string, fixedSalt: string): Promise<CryptoKey> {
        const enc: TextEncoder = new TextEncoder();
        const salt: Uint8Array<ArrayBuffer> = Uint8Array.from(atob(fixedSalt), c => c.charCodeAt(0));
        const keyMaterial: CryptoKey = await crypto.subtle.importKey('raw', enc.encode(password), { name: 'PBKDF2' }, false, ['deriveKey']);
        return crypto.subtle.deriveKey(
            {
                name: 'PBKDF2',
                salt,
                iterations: 100000,
                hash: 'SHA-256',
            },
            keyMaterial,
            { name: 'AES-GCM', length: 256 },
            false,
            ['encrypt', 'decrypt']
        );
    }

    static async symmetricEncrypt(payload: string, password: string, fixedSalt: string): Promise<string> {
        const enc = new TextEncoder();
        // const iv = Uint8Array.from(atob(fixedIv), c => c.charCodeAt(0));
        const iv: Uint8Array<ArrayBuffer> = crypto.getRandomValues(new Uint8Array(12));
        const hashedPassword = await SharedUtils.getHashString(password);        const key = await SharedUtils.deriveKeyFromPassword(hashedPassword, fixedSalt);

        const encrypted = await crypto.subtle.encrypt(
            { name: 'AES-GCM', iv },
            key,
            enc.encode(payload)
        );
        const combined: Uint8Array<ArrayBuffer> = new Uint8Array(iv.length + encrypted.byteLength);
        combined.set(iv);
        combined.set(new Uint8Array(encrypted), iv.length);
        return btoa(String.fromCharCode(...combined));
    }

    static async symmetricDecrypt(encryptedPayload: string, password: string, fixedSalt: string): Promise<string> {
        const dec: TextDecoder = new TextDecoder();
        const combined: Uint8Array<ArrayBuffer> = Uint8Array.from(atob(encryptedPayload), c => c.charCodeAt(0));
        //const iv = Uint8Array.from(atob(fixedIv), c => c.charCodeAt(0));
        const iv: Uint8Array<ArrayBuffer> = combined.slice(0, 12);
        const coreData: Uint8Array<ArrayBuffer> = combined.slice(12);
        const hashedPassword = await SharedUtils.getHashString(password);
        const key = await SharedUtils.deriveKeyFromPassword(hashedPassword, fixedSalt);

        // const encrypted = Uint8Array.from(atob(encryptedPayload), c => c.charCodeAt(0));
        const decrypted = await crypto.subtle.decrypt(
            { name: 'AES-GCM', iv },
            key,
            coreData
        );
        return dec.decode(decrypted);
    }

    /***********************************************************
     *      ENUMERATION FUNCTIONS
     ***********************************************************/

    static getEnumKey<T extends Record<string, string>>(enumObj: T, value: string): keyof T | undefined {
        return (Object.entries(enumObj).find(([_, v]) => v === value)?.[0]) as keyof T | undefined;
    }

    static getEnumValue<T extends Record<string, string>>(enumObj: T, key: string): T[keyof T] | undefined {
        return enumObj[key as keyof T];
    }

    /*********************************************************** */

    static getFiscalCodeDateOfBirth(fiscalCode: string): Date | null {
        const regex = /^([A-Z]{6})(\d{2})([A-Z])(\d{2})([A-Z]\d{3}[A-Z])$/;
        const match = fiscalCode.match(regex);
        if (!match) return null;

        const year = parseInt(match[2], 10);
        const monthLetter = match[3];
        const dayGender = parseInt(match[4], 10);

        // Map month letter to month index (0 = January)
        const monthsMap: { [key: string]: number } = {
            A: 0, B: 1, C: 2, D: 3, E: 4, H: 5, L: 6, M: 7, P: 8, R: 9, S: 10, T: 11
        };

        const month = monthsMap[monthLetter];
        if (month === undefined) return null;

        const isFemale = dayGender > 40;
        const day = isFemale ? dayGender - 40 : dayGender;
        const fullYear = year < 25 ? 2000 + year : 1900 + year;
        return new Date(fullYear, month, day + 1);
    }

    static getFiscalCodeGender(fiscalCode: string): 'M' | 'F' | null {
        const regex = /^([A-Z]{6})(\d{2})([A-Z])(\d{2})([A-Z]\d{3}[A-Z])$/;
        const match = fiscalCode.match(regex);
        if (!match) return null;

        const dayGender = parseInt(match[4], 10);
        if (isNaN(dayGender)) return null;

        return dayGender > 40 ? 'F' : 'M';
    }

    static getFiscalCodeBirthPlace(fiscalCode: string): string | null {
        const regex = /^([A-Z]{6})(\d{2})([A-Z])(\d{2})([A-Z])(\d{3})([A-Z])$/;
        const match = fiscalCode.match(regex);
        if (!match) return null;

        const placeCode = match[6]; // 3-digit place code (part of the 4-character birthplace)
        const checkChar = match[7];
        return placeCode + checkChar;
    }

    static isFiscalCodeValid(fiscalCode: string): boolean {
        const regex = /^[A-Z]{6}\d{2}[A-Z]\d{2}[A-Z]\d{3}[A-Z]$/;
        return regex.test(fiscalCode);
    }

    static randomUUID(): string {
        return crypto.randomUUID();
    }

    static getRolesFullIdentifiers(
        userRoles: EnumUserRole[],
        legalEntityRole?: EnumLegalEntityRole,
        userLegalEntityRoles?: EnumUserLegalEntityRole[]
    ): string[] {
        const roleFullIdentifiers: string[] = [];
        if (
            userRoles &&
            legalEntityRole &&
            userLegalEntityRoles &&
            userRoles.length > 0 &&
            legalEntityRole.length > 0 &&
            userLegalEntityRoles.length > 0
        ) {
            userRoles.forEach((userRole: EnumUserRole) => {
                if (userRole !== EnumUserRole.NONE) {
                    userLegalEntityRoles.forEach((userLegalEntityRole: EnumUserLegalEntityRole) => {
                        if (
                            userLegalEntityRole !== EnumUserLegalEntityRole.NONE &&
                            legalEntityRole !== EnumLegalEntityRole.NONE
                        ) {
                            const roleFullIdentifier: string = `${userRole}.${legalEntityRole}.${userLegalEntityRole}`;
                            roleFullIdentifiers.push(roleFullIdentifier);
                        }
                    });
                }
            });
        } else if (userRoles && userRoles.length > 0) {
            userRoles.forEach((userRole: EnumUserRole) => {
                if (userRole !== EnumUserRole.NONE) {
                    const roleFullIdentifier: string = `${userRole}.NONE.NONE`;
                    roleFullIdentifiers.push(roleFullIdentifier);
                }
            });
        }
        return roleFullIdentifiers;
    }

    static matchesRolePattern(userRole: string, pattern: string): boolean {
        const patternRegex = new RegExp('^' + pattern.replace(/\./g, '\\.').replace(/ANY/g, '[^. ]+') + '$');
        return patternRegex.test(userRole);
    }

    static matchesRolesPattern(userRoles: string[], pattern: string): boolean {
        const patternRegex = new RegExp('^' + pattern.replace(/\./g, '\\.').replace(/ANY/g, '[^. ]+') + '$');
        return userRoles.some(userRole => patternRegex.test(userRole));
    }

    static matchesRolesPatterns(userRoles: string[], patterns: string[]): boolean {
        return patterns.some(pattern => this.matchesRolesPattern(userRoles, pattern));
    }
}